/**
 * What can be played from the timeline: per source, its video files as one
 * chain and its audio files (design §7.2).
 *
 * @module
 */
import type { TimelineLane } from "../api/client";

export interface PlanFile {
  id: string;
  fromUtcMs: number;
  toUtcMs: number;
  /** How far its start time can be trusted (`High`, `Medium`, `Low`), and how it was found. */
  timeConfidence?: string | null;
  timeBasis?: string | null;
}

export interface SourcePlan {
  key: string;
  source: string;
  team: string;
  video: PlanFile[];
  /** Audio files, and the sound of video files. */
  audio: PlanFile[];
}

/** States in which a file has its proxy. */
const PLAYABLE = new Set(["ready", "partially_ready"]);
/** A file whose end is unknown is assumed to run this long, or until the next file. */
const UNKNOWN_END_MS = 10 * 60_000;

/** Lanes with an identified source; files sorted, overlaps clipped. */
export function planSources(lanes: readonly TimelineLane[]): SourcePlan[] {
  return lanes
    .filter((l) => l.source)
    .map((l) => {
      const files = (want: (f: TimelineLane["files"][number]) => boolean) => {
        const sorted = l.files
          .filter((f) => want(f) && PLAYABLE.has(f.state))
          .map((f) => ({
            id: f.id,
            fromUtcMs: Date.parse(f.start_utc),
            toUtcMs: f.end_utc ? Date.parse(f.end_utc) : NaN,
            timeConfidence: f.time_confidence,
            timeBasis: f.time_basis,
          }))
          .sort((a, b) => a.fromUtcMs - b.fromUtcMs);
        return sorted.map((f, i) => {
          const next = sorted[i + 1]?.fromUtcMs ?? Infinity;
          const end = Number.isNaN(f.toUtcMs) ? f.fromUtcMs + UNKNOWN_END_MS : f.toUtcMs;
          return { ...f, toUtcMs: Math.min(end, next) };
        });
      };
      const source = l.source ?? "";
      return {
        key: `${l.library_id}/${source}`,
        source,
        team: l.team_name,
        video: files((f) => f.kind === "video"),
        audio: files((f) => f.kind === "audio" || (f.kind === "video" && f.has_audio)),
      };
    })
    .filter((p) => p.video.length > 0 || p.audio.length > 0);
}

export interface Unplayable {
  key: string;
  source: string;
  team: string;
  reason: string;
}

const WAITING: Record<string, string> = {
  needs_review: "needs review",
  catalogued: "waiting for its working copy",
  deriving: "working copy being made",
};

/** Lanes with recordings but none the player can play yet, and why. */
export function unplayable(lanes: readonly TimelineLane[]): Unplayable[] {
  return lanes.flatMap((l) => {
    const media = l.files.filter((f) => f.kind === "video" || f.kind === "audio");
    if (!media.length || media.some((f) => PLAYABLE.has(f.state))) return [];
    const state = ["deriving", "catalogued", "needs_review"].find((s) =>
      media.some((f) => f.state === s),
    );
    const reason = !l.source
      ? "source not identified"
      : ((state && WAITING[state]) ?? "no working copy");
    return [
      {
        key: `${l.library_id}/${l.source ?? ""}`,
        source: l.source ?? "Unidentified",
        team: l.team_name,
        reason,
      },
    ];
  });
}

/** Index of the file playing at `utcMs`, else the next one; -1 if none. */
export function fileAt(files: readonly PlanFile[], utcMs: number): number {
  return files.findIndex((f) => f.toUtcMs > utcMs);
}
